// src/component/ProfileFavorites.jsx
import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";
import { FaHeart, FaRegHeart, FaShoppingCart, FaPlus, FaMinus } from "react-icons/fa";
import ProductModal from "./PriceProductModal";
import SafeImage from "./ui/SafeImage";

/* --------- общие утилиты --------- */
function itemsToUiMap(items = []) {
  const map = {};
  items.forEach((it) => {
    const pid = it.product ?? it.product_id;
    if (pid != null) map[pid] = Number(it.quantity) || 0;
  });
  return map;
}
function getProductStats(product) {
  const p = product || {};
  const stores = Array.isArray(p.stores) ? p.stores : [];
  const prices = stores.filter((s) => s.price != null).map((s) => Number(s.price));
  const minPrice = prices.length ? Math.min(...prices) : null;
  const totalQty = stores.reduce((acc, s) => acc + (Number(s.quantity) || 0), 0);
  return { minPrice, totalQty };
}
const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toLocaleString("ru-RU"));

export default function Favorites() {
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalProduct, setModalProduct] = useState(null);
  const [cartUi, setCartUi] = useState({}); // { productId: qty }

  /* --- монтирование: избранное + корзина --- */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // 1) список избранных id
        const { data, status } = await authFetch("/api/catalog/favorites/");
        if (status === 401) { setFavoriteIds([]); setProducts([]); setLoading(false); return; }
        const fav = Array.isArray(data) ? data : [];
        const ids = fav.map((f) => f.product_id);
        setFavoriteIds(ids);

        // 2) подробные карточки
        const detailed = await Promise.all(
          ids.map(async (id) => {
            const res = await fetch(`/api/catalog/product/${id}/`);
            return res.ok ? await res.json() : null;
          })
        );
        setProducts(detailed.filter(Boolean));
      } catch { setFavoriteIds([]); setProducts([]); }
      finally { setLoading(false); }
    })();

    // 3) актуальная корзина → локальная карта qty
    authFetch("/api/orders/cart/").then(({ data, status }) => {
      if (status === 200 && Array.isArray(data)) setCartUi(itemsToUiMap(data));
      else setCartUi({});
    }).catch(() => setCartUi({}));
  }, []);

  /* --- избранное on/off --- */
  const toggleFavorite = async (productId) => {
    try {
      const isFav = favoriteIds.includes(productId);
      const method = isFav ? "DELETE" : "POST";
      const { status } = await authFetch(`/api/catalog/favorites/${productId}/`, { method });
      if (status === 401) { alert("Чтобы работать с избранным, войдите в аккаунт."); return; }

      setFavoriteIds((prev) => (isFav ? prev.filter((id) => id !== productId) : [...prev, productId]));
      if (isFav) {
        setProducts((prev) => prev.filter((p) => p.id !== productId));
        setCartUi((prev) => { const copy = { ...prev }; delete copy[productId]; return copy; });
        if (modalProduct?.id === productId) setModalProduct(null);
      }
    } catch { alert("Ошибка изменения избранного. Попробуйте позже."); }
  };

  /* --- корзина: + --- */
  const addToCart = async (p, qty = 1) => {
    const { totalQty } = getProductStats(p);
    const have = cartUi[p.id] || 0;
    if (totalQty <= 0) return false;
    if (have + qty > totalQty) { alert("Нельзя добавить больше, чем есть в наличии."); return false; }

    const { data, status } = await authFetch("/api/orders/cart/add/", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: p.id, quantity: qty }),
    });
    if (status === 401) { alert("Войдите или зарегистрируйтесь!"); return false; }
    if (status === 409) { alert((data && data.error) || "Недостаточно товара на складе."); return false; }
    if (status < 200 || status >= 300) { alert("Не удалось добавить товар."); return false; }

    // Точная синхронизация если сервер вернул массив позиций
    if (Array.isArray(data)) setCartUi(itemsToUiMap(data));
    else setCartUi((m) => ({ ...m, [p.id]: (m[p.id] || 0) + qty }));
    return true;
  };

  /* --- корзина: − --- */
  const decFromCartUi = async (p) => {
    try {
      const { data: items, status } = await authFetch("/api/orders/cart/");
      if (status === 401) { alert("Чтобы изменить корзину, войдите в аккаунт."); return; }

      const item = (items || []).find(
        (it) => String(it.product ?? it.product_id) === String(p.id)
      );
      if (!item) {
        setCartUi((m) => { const copy = { ...m }; delete copy[p.id]; return copy; });
        return;
      }

      const newQty = Number(item.quantity) - 1;
      if (newQty <= 0) {
        await authFetch("/api/orders/cart/delete/", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ item_id: item.id }),
        });
      } else {
        await authFetch("/api/orders/cart/update/", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ item_id: item.id, quantity: newQty }),
        });
      }

      const { data: after } = await authFetch("/api/orders/cart/");
      setCartUi(itemsToUiMap(after || []));
    } catch { /* noop */ }
  };

  if (loading) return <div className="text-center text-gray-500">Загрузка избранного…</div>;
  if (products.length === 0) return <div className="text-center text-gray-500">Здесь появятся ваши избранные товары.</div>;

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Избранные товары</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p) => {
          const { minPrice, totalQty } = getProductStats(p);
          const isFav = favoriteIds.includes(p.id);
          const out = totalQty <= 0;
          const uiQty = cartUi[p.id] || 0;
          const canAddMore = uiQty < totalQty;
          const img = !p?.image ? "/no-image.svg" : p.image.startsWith?.("http") ? p.image : `/media/${p.image}`;

          return (
            <div key={p.id}
                 className="group relative bg-white rounded-xl shadow hover:shadow-orange-300 transition overflow-hidden flex flex-col cursor-pointer"
                 onClick={() => setModalProduct(p)}>
              {/* избранное */}
              <button className="absolute top-3 right-3 z-10"
                      onClick={(e) => { e.stopPropagation(); toggleFavorite(p.id); }}
                      title={isFav ? "Убрать из избранного" : "Добавить в избранное"}>
                {isFav ? <FaHeart size={20} color="red" /> : <FaRegHeart size={20} className="text-gray-500" />}
              </button>

              {/* картинка */}
              <div className="p-4 pb-0">
                <SafeImage src={img} alt={p.name} className="w-full h-44 object-contain rounded-t-xl select-none" fallback="/no-image.svg" />
              </div>

              {/* контент */}
              <div className="p-4 flex flex-col gap-2 grow">
                <h3 className="font-semibold text-gray-800 line-clamp-2">{p.name}</h3>

                <div className="mt-auto flex items-end justify-between">
                  <div className="flex flex-col">
                    <div className="text-orange-600 font-bold text-lg">{minPrice != null ? `${fmt(minPrice)} ₽` : "—"}</div>
                    <div className={`text-xs ${out ? "text-red-600" : "text-gray-500"}`}>
                      {out ? "Нет в наличии" : `В наличии всего: ${fmt(totalQty)} шт.`}
                    </div>
                  </div>

                  {/* корзина */}
                  {uiQty > 0 ? (
                    <div className="flex items-center gap-2 bg-green-600 text-white rounded-full px-3 py-1 sm:opacity-0 sm:group-hover:opacity-100 transition"
                         onClick={(e) => e.stopPropagation()}>
                      <button className="p-1 rounded-full bg-green-700 hover:bg-green-800" onClick={() => decFromCartUi(p)} aria-label="–">
                        <FaMinus size={10} />
                      </button>
                      <span className="min-w-[16px] text-center text-sm">{uiQty}</span>
                      <button className="p-1 rounded-full bg-green-700 hover:bg-green-800 disabled:opacity-50"
                              disabled={!canAddMore}
                              onClick={() => addToCart(p, 1)} aria-label="+"
                              title={canAddMore ? "Добавить ещё" : "Достигнут остаток"}>
                        <FaPlus size={10} />
                      </button>
                    </div>
                  ) : (
                    <button className={`p-2 rounded-full bg-green-600 hover:bg-green-700 text-white 
                                        ${out ? "opacity-40 pointer-events-none" : ""} 
                                        sm:opacity-0 sm:group-hover:opacity-100 transition`}
                            onClick={(e) => { e.stopPropagation(); addToCart(p, 1); }}
                            title={out ? "Нет в наличии" : "В корзину"}>
                      <FaShoppingCart />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {modalProduct && (
        <ProductModal
          open={!!modalProduct}
          onClose={() => setModalProduct(null)}
          product={modalProduct}
          isFavorite={favoriteIds.includes(modalProduct.id)}
          onToggleFavorite={toggleFavorite}
          onAddToCart={(qty = 1) => addToCart(modalProduct, qty)}
          onDecreaseOne={() => decFromCartUi(modalProduct)}
        />
      )}
    </div>
  );
}
